import { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { standardizeDate } from '../../../../../../../utils/voucherHelpers';
import dashboardData from '../../../../../../../data/dashboard/dashboardData.json';

const setupChecklistSteps = dashboardData.setupChecklistSteps || [];

export const useChecklistSteps = ({
  userData,
  agentProfile,
  stats,
  recentVouchers = [],
  hotelInputRef,
  isVoucherLocked,
  navigate
}) => {
  // Agency name extraction
  const agencyName =
    userData?.company_name ||
    agentProfile?.company_name ||
    userData?.name ||
    'ACS Tours & Travels';

  // Step completions logic
  const isRegistered = true;

  // Profile completion check: Requires logo, address, pincode, city, and company name to all be entered
  const rawLogo =
    userData?.logo ||
    userData?.company_logo ||
    userData?.logo_url ||
    userData?.agent?.logo ||
    agentProfile?.logo ||
    agentProfile?.logo_url ||
    agentProfile?.company_logo ||
    '';

  const hasLogo = Boolean(
    rawLogo &&
    typeof rawLogo === 'string' &&
    rawLogo.trim().length > 0 &&
    !rawLogo.includes('3607444.png') &&
    !rawLogo.includes('flaticon.com')
  );

  const rawAddress =
    userData?.address ||
    userData?.agent?.address ||
    agentProfile?.address ||
    agentProfile?.agent?.address ||
    '';

  const hasAddress = Boolean(
    rawAddress &&
    typeof rawAddress === 'string' &&
    rawAddress.trim().length > 0
  );

  const rawPincode =
    userData?.pincode !== undefined && userData?.pincode !== null && userData?.pincode !== ''
      ? userData.pincode
      : userData?.agent?.pincode !== undefined && userData?.agent?.pincode !== null && userData?.agent?.pincode !== ''
      ? userData.agent.pincode
      : agentProfile?.pincode !== undefined && agentProfile?.pincode !== null && agentProfile?.pincode !== ''
      ? agentProfile.pincode
      : agentProfile?.agent?.pincode;

  const hasPincode = Boolean(
    rawPincode !== undefined &&
    rawPincode !== null &&
    String(rawPincode).trim().length > 0
  );

  const rawCity =
    userData?.city ||
    userData?.agent?.city ||
    agentProfile?.city ||
    agentProfile?.agent?.city ||
    '';

  const hasCity = Boolean(
    rawCity &&
    typeof rawCity === 'string' &&
    rawCity.trim().length > 0
  );

  const rawCompanyName =
    userData?.company_name ||
    userData?.business_name ||
    userData?.agent?.company_name ||
    userData?.agent?.business_name ||
    agentProfile?.company_name ||
    agentProfile?.business_name ||
    agentProfile?.agent?.company_name ||
    agentProfile?.agent?.business_name ||
    '';

  const hasCompanyName = Boolean(
    rawCompanyName &&
    typeof rawCompanyName === 'string' &&
    rawCompanyName.trim().length > 0
  );

  const isProfileCustomized = Boolean(
    hasLogo &&
    hasAddress &&
    hasPincode &&
    hasCity &&
    hasCompanyName
  );
  const agentId = agentProfile?._id || userData?.agent?._id || userData?._id || '';

  // Clean up all legacy milestone keys from localStorage so they never falsely check steps
  useEffect(() => {
    try {
      localStorage.removeItem('trrip_has_traveller_on_tour');
      localStorage.removeItem('trrip_has_traveller_downloaded');
      localStorage.removeItem('trrip_has_created_voucher');
      if (agentId) {
        localStorage.removeItem(`trrip_has_traveller_on_tour_${agentId}`);
        localStorage.removeItem(`trrip_has_traveller_downloaded_${agentId}`);
        localStorage.removeItem(`trrip_has_created_voucher_${agentId}`);
      }
    } catch (e) {}
  }, [agentId]);

  // Real non-sample vouchers created by this agent
  const realVouchers = Array.isArray(recentVouchers)
    ? recentVouchers.filter((v) => v && !v.is_sample && !v.is_dummy && v.booking_id !== 'bysid' && v.booking_id !== 'by0id')
    : [];

  const isVoucherCreated = Boolean(
    agentProfile?.has_created_voucher ||
    userData?.has_created_voucher ||
    userData?.agent?.has_created_voucher ||
    stats?.hasCreatedVoucher ||
    (stats?.totalVouchers && stats.totalVouchers > 0) ||
    realVouchers.length > 0
  );
  const isAppShared = Boolean(
    agentProfile?.has_shared_app ||
    userData?.has_shared_app ||
    userData?.agent?.has_shared_app ||
    (agentId && (localStorage.getItem(`has_shared_app_${agentId}`) === 'true' || localStorage.getItem(`trrip_step_share_completed_${agentId}`) === 'true')) ||
    localStorage.getItem('has_shared_app') === 'true' ||
    localStorage.getItem('trrip_step_share_completed') === 'true'
  );
  const isTravellerDownloaded = Boolean(
    agentProfile?.has_traveller_downloaded ||
    userData?.has_traveller_downloaded ||
    userData?.agent?.has_traveller_downloaded ||
    stats?.hasTravellerDownloaded ||
    (stats?.installedVouchers && stats.installedVouchers > 0) ||
    realVouchers.some((v) =>
      (Array.isArray(v.active_guests) && v.active_guests.length > 0) ||
      (Array.isArray(v.guest_last_seen) && v.guest_last_seen.length > 0) ||
      v.is_downloaded === true ||
      ['installed', 'Installed'].includes(v.status)
    )
  );
  const todayStr = new Date().toISOString().split('T')[0];

  const isTravellerOnTour = Boolean(
    agentProfile?.has_traveller_on_tour ||
    agentProfile?.agent?.has_traveller_on_tour ||
    userData?.has_traveller_on_tour ||
    userData?.agent?.has_traveller_on_tour ||
    stats?.hasTravellerOnTour ||
    (stats?.onTourVouchers && stats.onTourVouchers > 0) ||
    realVouchers.some((v) => {
      const rawStart =
        v.tour_info?.start_date ||
        v.tour_info?.startDate ||
        v.start_date ||
        v.hotel_bookings?.[0]?.check_in_date ||
        v.travel_bookings?.[0]?.journey_date;
      if (rawStart) {
        const stdStart = standardizeDate(rawStart);
        if (stdStart && stdStart <= todayStr) {
          return true;
        }
      }
      return ['onTrip', 'On Trip', 'completed', 'Completed'].includes(v.status);
    })
  );

  const stepStatusMap = {
    register: {
      subtitle: agencyName,
      isCompleted: isRegistered,
      onAction: null,
    },
    brand: {
      isCompleted: isProfileCustomized,
      onAction: () => navigate('/profile'),
    },
    create: {
      isCompleted: isVoucherCreated,
      onAction: () => {
        if (isVoucherLocked) {
          navigate('/subscription');
          toast.error('Your trial or subscription has ended. Upgrade to create vouchers.');
        } else {
          hotelInputRef?.current?.click();
        }
      },
    },
    share: {
      isCompleted: isAppShared,
      onAction: null,
    },
    download: {
      isCompleted: isTravellerDownloaded,
      onAction: null,
    },
    ontour: {
      isCompleted: isTravellerOnTour,
      onAction: null,
    },
  };

  const steps = setupChecklistSteps.map((step) => {
    const dynamicData = stepStatusMap[step.id] || {};
    return {
      ...step,
      subtitle: dynamicData.subtitle !== undefined ? dynamicData.subtitle : step.subtitle,
      isCompleted: Boolean(dynamicData.isCompleted),
      onAction: dynamicData.onAction || null,
    };
  });

  const completedCount = steps.filter((s) => s.isCompleted).length;
  const progressPercentage = Math.round((completedCount / steps.length) * 100);
  const isAllCompleted = completedCount === steps.length;

  return {
    steps,
    completedCount,
    progressPercentage,
    isAllCompleted
  };
};

const SetupChecklistTab = ({
  steps,
  completedCount,
  progressPercentage,
  isSideBySide
}) => {
  const checklistContainerRef = useRef(null);

  // Auto-scroll checklist so that if 2+ steps are completed, only 1 tick is shown at top
  useEffect(() => {
    if (checklistContainerRef.current) {
      const firstUncompletedIdx = steps.findIndex((s) => !s.isCompleted);
      if (firstUncompletedIdx > 1) {
        checklistContainerRef.current.scrollTop = (firstUncompletedIdx - 1) * 46;
      } else {
        checklistContainerRef.current.scrollTop = 0;
      }
    }
  }, [completedCount, steps]);

  return (
    <div className="flex-1 flex flex-col min-h-0 animate-in fade-in duration-200">
      {/* Header with progress info */}
      <div className="shrink-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500 font-normal">
            {completedCount} of {steps.length} done
          </p>

          {/* Progress Pill */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-normal border border-primary/20 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            <span>{progressPercentage}%</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">
          <div
            className="bg-primary h-full rounded-full transition-all duration-700 ease-out"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Checklist Items */}
      <div
        ref={checklistContainerRef}
        className={`flex-1 overflow-y-auto pr-1 mt-2.5 space-y-2.5 min-h-0 scroll-smooth ${
          isSideBySide ? '' : 'max-h-[320px]'
        }`}
      >
        {steps.map((step) => (
          <div key={step.id} className="flex items-center justify-between gap-2 py-0.5 group">
            <div className="flex items-start gap-2.5 min-w-0">
              {step.isCompleted ? (
                <div className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shrink-0 mt-0.5 shadow-2xs">
                  <Check className="w-3 h-3 stroke-[2.5]" />
                </div>
              ) : (
                <div className="w-5 h-5 rounded-full border-2 border-slate-300 group-hover:border-primary/60 shrink-0 mt-0.5 transition-colors"></div>
              )}

              <div className="min-w-0 flex-1">
                <div className="text-xs font-normal text-slate-800 leading-tight">
                  {step.title}
                </div>
                <div className="text-[11px] font-normal text-slate-500 mt-0.5 leading-snug">
                  {step.subtitle}
                </div>
              </div>
            </div>

            {!step.isCompleted && step.actionText && (
              <button
                type="button"
                onClick={step.onAction}
                className="text-[11px] font-normal text-primary hover:text-[#056564] shrink-0 transition-colors py-0.5 px-2 rounded-lg bg-primary/5 hover:bg-primary/10 cursor-pointer"
              >
                {step.actionText}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SetupChecklistTab;
